import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { ref, get, set } from "firebase/database";
import { database } from "../../firebaseConfig";
import "./HeadMetaConfigEditor.css";
import { config } from "../../config/config"; // Ensure you have a config file with baseUrl
const API_URL = config.baseUrl;

// Default head meta configuration.
const defaultHeadMetaConfig = {
    charset: "utf-8",
    icon: "https://www.marriottpharmaceutical.com/favicon.png",
    viewport: "width=device-width, initial-scale=1",
    themeColor: "#000000",
    description: "Web site created using create-react-app",
    appleTouchIcon: "https://www.marriottpharmaceutical.com/logo192.png",
    manifest: "https://www.marriottpharmaceutical.com/manifest.json",
    title: "React App"
};

const HeadMetaConfigEditor = () => {
    const [headMetaConfig, setHeadMetaConfig] = useState(defaultHeadMetaConfig);
    const [loading, setLoading] = useState(true);

    // Refs for file inputs
    const iconInputRef = useRef(null);
    const appleIconInputRef = useRef(null);

    // Fetch head meta configuration from Firebase.
    useEffect(() => {
        const fetchHeadMetaConfig = async () => {
            try {
                const snapshot = await get(ref(database, "headmeta"));
                if (snapshot.exists()) {
                    setHeadMetaConfig({ ...defaultHeadMetaConfig, ...snapshot.val() });
                }
            } catch (error) {
                console.error("Error fetching head meta config:", error);
            }
            setLoading(false);
        };
        fetchHeadMetaConfig();
    }, []);

    // Helper: Upload file to server.
    const uploadFile = async (file) => {
        if (!file) return "";
        const formData = new FormData();
        formData.append("media", file);
        try {
            const response = await axios.post(`${API_URL}upload`, formData, {
                headers: { "Content-Type": "multipart/form-data" }
            });
            return response.data.fileUrl || "";
        } catch (error) {
            console.error("Error uploading file:", error);
            return "";
        }
    };

    // Helper: Delete file from server.
    const deleteFile = async (fileUrl) => {
        if (!fileUrl) return;
        const parts = fileUrl.split("/");
        const filename = parts[parts.length - 1];
        try {
            await axios.delete(`${API_URL}delete`, { data: { filename } });
            console.log("Deleted file:", filename);
        } catch (error) {
            console.error("Error deleting file:", error);
        }
    };

    // Handlers for file upload for icon and apple touch icon.
    const handleIconChange = async (e) => {
        const file = e.target.files[0];
        if (file) {
            const fileUrl = await uploadFile(file);
            setHeadMetaConfig((prev) => ({ ...prev, icon: fileUrl }));
        }
    };

    const handleAppleIconChange = async (e) => {
        const file = e.target.files[0];
        if (file) {
            const fileUrl = await uploadFile(file);
            setHeadMetaConfig((prev) => ({ ...prev, appleTouchIcon: fileUrl }));
        }
    };

    const handleDeleteIcon = () => {
        deleteFile(headMetaConfig.icon);
        setHeadMetaConfig((prev) => ({ ...prev, icon: "" }));
    };

    const handleDeleteAppleIcon = () => {
        deleteFile(headMetaConfig.appleTouchIcon);
        setHeadMetaConfig((prev) => ({ ...prev, appleTouchIcon: "" }));
    };

    // Handle changes for other text fields.
    const handleChange = (e) => {
        const { name, value } = e.target;
        setHeadMetaConfig((prev) => ({ ...prev, [name]: value }));
    };

    // Delete a generic field.
    const handleDeleteField = (fieldName) => {
        setHeadMetaConfig((prev) => {
            const newConfig = { ...prev };
            delete newConfig[fieldName];
            return newConfig;
        });
    };

    // Add a new custom field.
    const handleAddField = () => {
        const fieldName = prompt("Enter new meta field name (e.g., 'author'):");
        if (!fieldName) return;
        if (headMetaConfig[fieldName]) {
            alert("Field already exists!");
            return;
        }
        const fieldValue = prompt("Enter the value for this field:");
        setHeadMetaConfig((prev) => ({ ...prev, [fieldName]: fieldValue }));
    };

    // Save configuration to Firebase.
    const handleSaveConfig = async () => {
        try {
            await set(ref(database, "headmeta"), headMetaConfig);
            alert("Head meta configuration saved successfully!");
        } catch (error) {
            console.error("Error saving head meta config:", error);
            alert("Error saving configuration.");
        }
    };

    if (loading) {
        return (
            <div className="HeadMetaConfigEditor-loading">
                Loading Head Meta configuration...
            </div>
        );
    }

    return (
        <div className="HeadMetaConfigEditor">
            <h2>Edit Head Meta Configuration</h2>
            <div className="HeadMetaConfigEditor-form">
                {/* Favicon (icon) upload */}
                <div className="HeadMetaConfigEditor-form-group">
                    <label htmlFor="icon">Favicon (Icon):</label>
                    {headMetaConfig.icon && (
                        <div className="HeadMetaConfigEditor-logo-preview">
                            <img src={headMetaConfig.icon} alt="Favicon Preview" />
                            <button onClick={handleDeleteIcon}>Delete</button>
                        </div>
                    )}
                    <input
                        type="file"
                        accept="image/*"
                        ref={iconInputRef}
                        onChange={handleIconChange}
                    />
                </div>

                {/* Apple Touch Icon upload */}
                <div className="HeadMetaConfigEditor-form-group">
                    <label htmlFor="appleTouchIcon">Apple Touch Icon:</label>
                    {headMetaConfig.appleTouchIcon && (
                        <div className="HeadMetaConfigEditor-logo-preview">
                            <img src={headMetaConfig.appleTouchIcon} alt="Apple Touch Icon Preview" />
                            <button onClick={handleDeleteAppleIcon}>Delete</button>
                        </div>
                    )}
                    <input
                        type="file"
                        accept="image/*"
                        ref={appleIconInputRef}
                        onChange={handleAppleIconChange}
                    />
                </div>

                {/* Other text-based meta fields */}
                {Object.entries(headMetaConfig).map(([key, value]) => {
                    // Skip file fields since they have their own UI.
                    if (key === "icon" || key === "appleTouchIcon") return null;
                    return (
                        <div key={key} className="HeadMetaConfigEditor-form-group">
                            <label htmlFor={key}>{key}</label>
                            <input
                                type="text"
                                id={key}
                                name={key}
                                value={value}
                                onChange={handleChange}
                            />
                            <button onClick={() => handleDeleteField(key)}>Delete</button>
                        </div>
                    );
                })}
            </div>
            <div className="HeadMetaConfigEditor-buttons">
                <button onClick={handleAddField} className="HeadMetaConfigEditor-add-field">
                    Add New Field
                </button>
                <button onClick={handleSaveConfig} className="HeadMetaConfigEditor-save-button">
                    Save Head Meta Configuration
                </button>
            </div>
        </div>
    );
};

export default HeadMetaConfigEditor;
